import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import TeamContext, { View } from '@atom/components/teamPortal/TeamContext';
import UserGroupOptions from '@atom/components/teamPortal/teamControls/UserGroupOptions';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, LensIcon } from '@atom/mui/icons';
import { UserGroupTreeType } from '@atom/types/userGroups';
import { getColorFromColorId } from '@atom/utilities/colorUtilities';

import classes from './userGroupTree.module.css';

const PADDING_OFFSET = 0.75;

const styles = {
  iconButton: {
    margin: '0 9px',
  },
  indentation: {
    paddingLeft: '3rem',
  },
  indentationNoArrow: {
    paddingLeft: '3.25rem',
  },
};

export interface Props {
  userGroup: UserGroupTreeType;
  indentation: number;
  path: UserGroupTreeType[];
}

const UserGroupRow = ({ userGroup, indentation, path }: Props) => {
  const [{ view }, { updateSearchParams }] = useNavigation({
    view: SearchParamType.STRING,
  });
  const { handleGroup, open, toggleOpen, activeGroup } =
    useContext(TeamContext);

  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [userGroup.name]);

  useEffect(() => {
    if (activeGroup?.id && activeGroup?.id === userGroup.id) {
      handleGroup(userGroup, path, true);
    }
  }, [activeGroup?.id]);

  const expanded = open.has(userGroup.id);
  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;
  const renderExpandedArrow = !R.isEmpty(userGroup.userGroups);

  const onClick = (selectedUserGroup: UserGroupTreeType) => {
    updateSearchParams({ view: View.DIRECTORY }, false);
    handleGroup(selectedUserGroup, path);
  };

  const leftIcon = (
    <>
      {renderExpandedArrow && (
        <IconButton
          onClick={() => toggleOpen(userGroup.id)}
          size="small"
          style={styles.iconButton}
        >
          <Icon icon={arrowDirection} />
        </IconButton>
      )}
      <Icon color={getColorFromColorId(userGroup.colorId)} icon={LensIcon} />
    </>
  );

  const containerStyle = {
    paddingLeft: `calc(${PADDING_OFFSET * indentation}rem)`,
  };

  const overflowStyle = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: `calc(12.5rem - (${PADDING_OFFSET * indentation}rem))`,
  };

  return (
    <>
      <div
        className={clsx(
          classes.row,
          view === View.DIRECTORY &&
            activeGroup?.id === userGroup.id &&
            classes.selected,
        )}
        style={containerStyle}
        onClick={() => onClick(userGroup)}
      >
        <div
          className={clsx(classes.rowContent, classes.subRow)}
          style={
            !userGroup.userGroups.length
              ? styles.indentationNoArrow
              : renderExpandedArrow
                ? {}
                : styles.indentation
          }
        >
          <div ref={container} className={classes.leftContent}>
            {leftIcon}
            <div className={classes.nameContainer}>
              <div className={classes.title}>
                {!overflowing ? (
                  userGroup.name
                ) : (
                  <Tooltip
                    lightTooltip
                    title={userGroup.name}
                    placement="right-start"
                  >
                    <p style={overflowStyle} className={classes.title}>
                      {userGroup.name}
                    </p>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          <UserGroupOptions userGroup={userGroup} />
        </div>
      </div>
      {expanded ? (
        userGroup.userGroups.map((item: UserGroupTreeType) => (
          <UserGroupRow
            key={item.id}
            userGroup={item}
            indentation={indentation + 1}
            path={[...path, item]}
          />
        ))
      ) : (
        <div />
      )}
    </>
  );
};

export default UserGroupRow;
